import { watch, type PropType } from 'vue';
import {
  useSearchParams,
  useDocumentSearchTypeStore,
  type SearchContext,
  type SearchFilter,
  type SearchSort,
} from '@/shared/lib';
import { useDocumentRevisionSearch } from '@/entities/Document';

export interface UseDocumentListSearchProps {
  searchTerm?: string;
  searchSort?: SearchSort;
  filter?: SearchFilter;
  page?: number;
  amount?: number;
  searchUrl: string;
  context?: 'search' | 'gestao';
}

export const documentListSearchProps = {
  searchTerm: { type: String, default: '' },
  searchSort: { type: Object as PropType<SearchSort>, default: () => ({}) },
  filter: { type: Object, default: () => ({}) },
  page: { type: Number, default: 1 },
  amount: { type: Number, default: 10 },
  searchUrl: { type: String, default: '/document-revisions/search' },
  context: { type: String as PropType<'search' | 'gestao'>, default: undefined },
} as const;

export default function useDocumentListSearch(
  props: UseDocumentListSearchProps,
  defaultContext?: SearchContext
) {
  const { searchType } = useDocumentSearchTypeStore();
  const { searchParams } = useSearchParams(props, defaultContext, searchType);

  const searchComposable = useDocumentRevisionSearch(searchParams.value);
  const {
    items: revisions,
    metadata,
    allItems: allRevisions,
    loadingMore,
    loading,
    hasMoreItems: hasMoreRevisions,
    selectedQuantity,
    shouldShowTable,
    shouldShowQuantitySelect,
    shouldShowNoResults,
    shouldShowEmptyPage,
    handleSearch,
    handleSortOptions,
    handleFilter,
    applyListQueryReset,
    onPageChanged,
    handleQuantityChange,
    initSearch,
    removeRevisions,
    cleanup,
    sort,
    order,
    filter,
    isInfiniteScrollMode,
    shouldShowNoFilterResults,
  } = searchComposable;

  watch(
    [() => props.searchTerm, () => props.searchSort, () => props.filter],
    async ([newTerm, newSort, newFilter], [oldTerm, oldSort, oldFilter]) => {
      const termChanged = newTerm !== undefined && newTerm !== oldTerm;
      const sortChanged = JSON.stringify(newSort) !== JSON.stringify(oldSort);
      const filterChanged = JSON.stringify(newFilter) !== JSON.stringify(oldFilter);
      const hasSortOptions = newSort?.sort !== undefined && newSort?.order !== undefined;

      if ([termChanged, sortChanged, filterChanged].filter(Boolean).length > 1) {
        await applyListQueryReset({
          search: newTerm,
          filter: (newFilter ?? {}) as SearchFilter,
          sort: hasSortOptions ? { sort: newSort.sort!, order: newSort.order! } : undefined,
        });
        return;
      }

      if (termChanged) {
        await handleSearch(newTerm);
      }
      if (filterChanged) {
        await handleFilter((newFilter ?? {}) as SearchFilter);
      }
      if (sortChanged && hasSortOptions) {
        await handleSortOptions(newSort.sort!, newSort.order!);
      }
    },
    { deep: true }
  );

  return {
    revisions,
    metadata,
    allRevisions,
    loadingMore,
    loading,
    hasMoreRevisions,
    selectedQuantity,
    shouldShowTable,
    shouldShowQuantitySelect,
    shouldShowNoFilterResults,
    shouldShowNoResults,
    shouldShowEmptyPage,
    handleSearch,
    handleSortOptions,
    handleFilter,
    onPageChanged,
    handleQuantityChange,
    initSearch,
    removeRevisions,
    cleanup,
    sort,
    order,
    filter,
    searchTerm: searchComposable.searchTerm,
    isInfiniteScrollMode,
  };
}
